import { useMemo } from 'react';

import { useSupplierInvoices } from '../../../hooks/useSupplierLedger';
import {
  getClassificationFieldLabel,
  getClassificationSummaryLabel,
  getInvoiceMonthKey,
  hasGeneratedPaymentOrder,
} from '../utils/supplierLedgerHelpers';
import useSupplierReportsData from './useSupplierReportsData';

const useSupplierDashboardData = () => {
  const {
    providerRows,
    conceptRows,
    classificationRows,
    providerLoading,
    conceptLoading,
    classificationLoading,
  } = useSupplierReportsData();
  const { data: invoices = [], isLoading: invoicesLoading, isFetching: invoicesFetching } = useSupplierInvoices();
  const reportsLoading = providerLoading || conceptLoading || classificationLoading;
  const loading = reportsLoading || invoicesLoading || (invoicesFetching && !invoices.length);
  const activeInvoices = useMemo(
    () => invoices.filter((invoice) => invoice.status !== 'cancelled'),
    [invoices],
  );

  const totals = useMemo(() => providerRows.reduce((acc, row) => ({
    invoiced: acc.invoiced + Number(row.total_invoiced_ars || 0),
    paid: acc.paid + Number(row.paid_amount_ars || 0),
    pending: acc.pending + Number(row.pending_amount_ars || 0),
    balance: acc.balance + Number(row.ledger_balance_ars || 0),
  }), {
    invoiced: 0,
    paid: 0,
    pending: 0,
    balance: 0,
  }), [providerRows]);

  const monthlyData = useMemo(() => {
    const grouped = new Map();
    activeInvoices.forEach((invoice) => {
      const month = getInvoiceMonthKey(invoice);
      if (!month) return;
      const item = grouped.get(month.key) || {
        key: month.key,
        label: month.label,
        facturas: 0,
        facturado: 0,
        pendiente: 0,
      };
      item.facturas += 1;
      item.facturado += Number(invoice.total_amount_ars || 0);
      item.pendiente += Number(invoice.pending_amount_ars ?? ((invoice.total_amount_ars || 0) - (invoice.paid_amount_ars || 0)));
      grouped.set(month.key, item);
    });

    return [...grouped.values()]
      .sort((left, right) => left.key.localeCompare(right.key))
      .slice(-12);
  }, [activeInvoices]);

  const topProviderData = useMemo(() => providerRows
    .filter((row) => Number(row.ledger_balance_ars || 0) > 0)
    .map((row) => ({
      name: row.provider_name || 'Sin proveedor',
      value: Number(row.ledger_balance_ars || 0),
    }))
    .sort((left, right) => right.value - left.value)
    .slice(0, 8), [providerRows]);

  const topConceptData = useMemo(() => conceptRows
    .filter((row) => Number(row.total_amount_ars || 0) > 0)
    .map((row) => ({
      name: row.concept || 'Sin concepto',
      value: Number(row.total_amount_ars || 0),
    }))
    .sort((left, right) => right.value - left.value)
    .slice(0, 8), [conceptRows]);

  const classificationData = useMemo(() => classificationRows
    .filter((row) => Number(row.total_amount_ars || 0) > 0 || Number(row.pending_amount_ars || 0) > 0)
    .map((row) => ({
      name: getClassificationSummaryLabel(row),
      total: Number(row.total_amount_ars || 0),
      pendiente: Number(row.pending_amount_ars || 0),
    }))
    .slice(0, 8), [classificationRows]);

  const areaData = useMemo(() => {
    const grouped = new Map();
    classificationRows.forEach((row) => {
      const area = getClassificationFieldLabel('cost_area', row.cost_area);
      grouped.set(area, (grouped.get(area) || 0) + Number(row.total_amount_ars || 0));
    });
    return [...grouped.entries()]
      .map(([name, value]) => ({ name, value }))
      .filter((row) => row.value > 0)
      .sort((left, right) => right.value - left.value);
  }, [classificationRows]);

  const pendingProviders = providerRows.filter((row) => Number(row.ledger_balance_ars || 0) > 0).length;
  const generatedOrders = activeInvoices.filter(hasGeneratedPaymentOrder).length;
  const averageInvoice = activeInvoices.length ? totals.invoiced / activeInvoices.length : 0;
  const paidRatio = totals.invoiced > 0 ? (totals.paid / totals.invoiced) * 100 : 0;

  return {
    activeInvoices,
    areaData,
    averageInvoice,
    classificationData,
    generatedOrders,
    loading,
    monthlyData,
    paidRatio,
    pendingProviders,
    topConceptData,
    topProviderData,
    totals,
  };
};

export default useSupplierDashboardData;
